import React from 'react';
import { useForm, usePage } from '@inertiajs/react';
import Layout from '../components/Layout.jsx';

function Field({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      {children}
      {error && <p className="mt-1.5 text-xs font-medium text-rose-600">{error}</p>}
    </div>
  );
}

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

const disabledInputClass =
  "block w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500 cursor-not-allowed";

function IdentityCard({ user }) {
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Your account';
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 rounded-2xl p-6 sm:p-8 shadow-lg text-white">
      <div className="absolute -right-20 -top-20 w-72 h-72 bg-white/20 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute right-1/3 -bottom-24 w-64 h-64 bg-purple-300/25 rounded-full blur-3xl" aria-hidden="true" />

      <div className="relative flex items-center gap-5">
        <div className="shrink-0 w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-3xl font-bold border border-white/20">
          {initials}
        </div>
        <div className="min-w-0">
          <h2 className="text-2xl sm:text-3xl font-bold truncate">{fullName}</h2>
          <p className="text-sm text-indigo-100 truncate">{user.email}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {user.student_type && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/10 border border-white/20 backdrop-blur-sm">
                {user.student_type}
              </span>
            )}
            {user.email_verified ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-400/20 text-emerald-100 border border-emerald-300/30">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-400/20 text-amber-100 border border-amber-300/30">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                Email not verified
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ icon, title, description, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

const LIST_TYPE_LABELS = {
  active: 'Active certificate',
  pending_renewal: 'Pending renewal',
  pending_initial: 'Pending initial',
};

function PublicLicenseSuggestions({ suggestions, onApply }) {
  if (!suggestions?.length) return null;
  return (
    <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 mt-0.5 shrink-0 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-indigo-900">
            We found {suggestions.length === 1 ? 'a possible license match' : `${suggestions.length} possible license matches`} in the California ACB public records.
          </p>
          <p className="mt-0.5 text-xs text-indigo-800">Apply one to prefill your license number and expiration. You can still edit before saving.</p>
          <ul className="mt-3 space-y-2">
            {suggestions.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-white px-3 py-2">
                <div className="min-w-0 text-sm">
                  <div className="font-semibold text-gray-900 truncate">
                    #{s.certificate_number}
                    {s.facility_type && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{s.facility_type}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {LIST_TYPE_LABELS[s.list_type] || s.list_type}
                    {s.expiration_date && <span> · Expires {s.expiration_date}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onApply(s)}
                  className="shrink-0 inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition"
                >
                  Apply
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Profile({ user, public_license_suggestions: publicLicenseSuggestions = [] }) {
  const { flash } = usePage().props;

  const { data, setData, patch, processing, errors, isDirty } = useForm({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    license_number: user.license_number || '',
    license_expiration_date: user.license_expiration_date || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    patch('/profile');
  };

  const applySuggestion = (s) => {
    setData({
      ...data,
      license_number: s.certificate_number || '',
      license_expiration_date: s.expiration_date || '',
    });
  };

  const showSuggestions = !data.license_number && publicLicenseSuggestions.length > 0;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Profile</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your account details and credentials.</p>
        </div>

        {flash?.notice && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="font-medium">{flash.notice}</p>
          </div>
        )}

        <IdentityCard user={user} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <SectionCard
            title="Personal Information"
            description="The name we'll use on certificates and emails."
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <Field id="email" label="Email">
                <input
                  id="email"
                  type="email"
                  value={user.email}
                  disabled
                  className={disabledInputClass}
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="first_name" label="First name" error={errors.first_name}>
                  <input
                    id="first_name"
                    type="text"
                    value={data.first_name}
                    onChange={(e) => setData('first_name', e.target.value)}
                    className={inputClass}
                    placeholder="Jane"
                  />
                </Field>
                <Field id="last_name" label="Last name" error={errors.last_name}>
                  <input
                    id="last_name"
                    type="text"
                    value={data.last_name}
                    onChange={(e) => setData('last_name', e.target.value)}
                    className={inputClass}
                    placeholder="Doe"
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Credentials"
            description="Optional — used on certificates when present."
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            }
          >
            {showSuggestions && (
              <PublicLicenseSuggestions suggestions={publicLicenseSuggestions} onApply={applySuggestion} />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="license_number" label="License number" error={errors.license_number}>
                <input
                  id="license_number"
                  type="text"
                  value={data.license_number}
                  onChange={(e) => setData('license_number', e.target.value)}
                  className={inputClass}
                  placeholder="Optional"
                />
              </Field>
              <Field id="license_expiration_date" label="License expiration" error={errors.license_expiration_date}>
                <input
                  id="license_expiration_date"
                  type="date"
                  value={data.license_expiration_date}
                  onChange={(e) => setData('license_expiration_date', e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>
          </SectionCard>

          <div className="flex items-center justify-between gap-3 sticky bottom-4 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-500">
              {isDirty ? 'You have unsaved changes.' : 'All changes saved.'}
            </p>
            <button
              type="submit"
              disabled={processing || !isDirty}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {processing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Saving…
                </>
              ) : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
